import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Slide, Fade, Skeleton } from "@mui/material";
import {
  delayTime,
  fadedelayTime,
  slideDirection,
} from "../../../../utils/transitionEffectParams";
import { loadColumnPreference } from "../../../common/CustomColumn/customize_column";
import { PutColumnPreference } from "../../../../api/column_pref";
import ListHeading from "../../../common/RDTListHeading/ListHeading";
import BreadcrumbHeading from "../../../common/RDTListHeading/BreadcrumbHeading";
import Images from "../../../../utils/images";
import DataTable from "react-data-table-component";
import useWindowDimensions from "../../../../utils/getHeightWidth";
import { getScanResults, checkIPReachability, checkBatchIPReachability, refreshResourceStatus } from "../../../../api/cloudDiscovery";
import { FiRefreshCw } from "react-icons/fi";
import { IconButton, Tooltip } from "@mui/material";
import Nav from "react-bootstrap/Nav";
import NavDropdown from "react-bootstrap/NavDropdown";
import { LazyLoadImage } from "react-lazy-load-image-component";
import SubHeaderComponent from "../../../common/RDTSubHeaderComponent/SubHeaderComponent";
import { showError, showSuccess } from "../../../../utils/showMessage";
import DataTableSkeleton from "../../../common/Skeletons/DataTableSkeleton";
import "../CloudConfiguration.scss";
import { onboardAssets } from "../../../../api/cloudDiscovery";
import GetCapabilities from "../../../../utils/getCapabilities.js";
import CustomModal from "../../../common/CustomModal/CustomModal";
import ShowAlert from "../../../common/ShowAlert/ShowAlert";
import { useTranslation } from "react-i18next";

export default function ScanResults() {
  const { t } = useTranslation();
  const { scan_id, historyId, config_id } = useParams();
  const scanId = scan_id || historyId;
  const configId = config_id;
  const { height } = useWindowDimensions();
  const navigate = useNavigate();
  const my_capabilities = GetCapabilities();
  const isCapable = my_capabilities[0];
  
  useEffect(() => {
    if (!isCapable?.cloud_discovery?.view_scan_results) {
      navigate("/dashboard/discovery/cloudconfiguration");
    }
  }, [isCapable, navigate]);

  const [slideChecked, setSlideChecked] = useState(false);
  const [resultsList, setResultsList] = useState([]);
  const [pending, setPending] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [preferenceLoaded, setPreferenceLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [filterBy, setFilterBy] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [list, setList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [action, setAction] = useState("edit_and_onboard");
  const actionOptions = [
    { label: t("cloudDiscovery.onboard"), value: "edit_and_onboard" }
  ];
  const [openEditOnboardModal, setOpenEditOnboardModal] = useState(false);
  const [resourcesToEditOnboard, setResourcesToEditOnboard] = useState([]);
  const [resourceNames, setResourceNames] = useState([]);
  const [reachabilityStatus, setReachabilityStatus] = useState({});
  const [refreshingStatusRowId, setRefreshingStatusRowId] = useState(null);
  const checkingQueueRef = useRef([]);
  const isCheckingRef = useRef(false);
  const checkingIntervalRef = useRef(null);
  const tableHeightWithData = height - 220 + "px";
  const tableHeightEmpty = height - 220 + "px";
  const [datatableHeight, setDatatableHeight] = useState(tableHeightEmpty);
  const pages_url = "dashboard/discovery/cloudconfiguration/history/results";

  const page_heading = t("cloudDiscovery.scanHistory");

  const customizeColumnDict = {
    "common.type": true,
    "reports.status": true,
    "siem.protocol": true,
    "cloudDiscovery.region": true,
    "cloudDiscovery.publicIp": true,
    "cloudDiscovery.privateIp": true,
    "cloudDiscovery.reachable": true,
    "networkConfiguration.onboarded": true,
  };
  const [checkedStateDict, setCheckedStateDict] = useState(customizeColumnDict);
  const [colCount, setColCount] = useState(null);
  const filterOptions = [
    { label: t("cloudDiscovery.instanceId"), value: "instanceid" },
    { label: t("common.name"), value: "name" },
    { label: t("common.type"), value: "type" },
    { label: t("ldap.protocol"), value: "protocol" },
    { label: t("common.status"), value: "status" },
    { label: t("cloudDiscovery.region"), value: "region" },
    { label: t("cloudDiscovery.publicIp"), value: "publicip" },
    { label: t("cloudDiscovery.privateIp"), value: "privateip" },
    { label: t("cloudDiscovery.onboarded"), value: "is_onboarded" }
  ];
  const handleSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) {
      setFilteredData(list);
    } else {
      const lowerSearch = search.toLowerCase();
      const filtered = list.filter(row =>
        String(row.instanceid || "").toLowerCase().includes(lowerSearch) ||
        String(row.name || "").toLowerCase().includes(lowerSearch) ||
        String(row.type || row.service || "").toLowerCase().includes(lowerSearch) ||
        String(row.protocol || "").toLowerCase().includes(lowerSearch) ||
        String(row.status || "").toLowerCase().includes(lowerSearch) ||
        String(row.region || "").toLowerCase().includes(lowerSearch) ||
        String(row.publicip || "").toLowerCase().includes(lowerSearch) ||
        String(row.privateip || "").toLowerCase().includes(lowerSearch)
      );
      setFilteredData(filtered);
    }
  };
  useEffect(() => {
    loadColumnPreference(setCheckedStateDict, pages_url, checkedStateDict, rowsPerPage, setRowsPerPage, setPreferenceLoaded);
  }, []);

  useEffect(() => {
    if (!preferenceLoaded) return;
    setPending(true);
    fetchScanResults();
  }, [page, rowsPerPage, preferenceLoaded]);

  const fetchScanResults = async () => {
    try {
      setPending(true);

      const { data, error } = await getScanResults({
        historyId: scanId,
        page,
        pageSize: rowsPerPage,
      });

      if (error) {
        showError(error?.message || "Failed to fetch scan results.");
        setResultsList([]);
        setFilteredData([]);
        setList([]);
        setTotal(0);
        return;
      }

      if (data) {
        const resources = (data.results || data.resources || []).map(resource => {
          const metadata = resource.metadata || {};
          const platform = metadata.platform || resource.platform || "";
          
          let protocol = "-";
          let protocolPort = null;
          if (platform.toLowerCase().includes("windows")) {
            protocol = "RDP";
            protocolPort = 3389;
          } else if (platform.toLowerCase().includes("linux") || platform.toLowerCase().includes("unix")) {
            protocol = "SSH/SFTP";
            protocolPort = 22;
          }
          
          return {
            id: resource.id,
            service: resource.resource_type || resource.service || "-",
            status: metadata.state || metadata.status || resource.status || "-",
            name: resource.name || metadata.name || "-",
            instanceid: resource.provider_resource_id || resource.instanceid || "-",
            provider_resource_id: resource.provider_resource_id || resource.instanceid || resource.id,
            region: metadata.region || resource.region || "-",
            type: resource.resource_type || resource.type || "-",
            publicip: metadata.public_ip || metadata.publicip || resource.publicip || "-",
            privateip: metadata.private_ip || metadata.privateip || resource.privateip || "-",
            keyname: metadata.key_name || metadata.keyname || resource.keyname || "-",
            is_onboarded: resource.is_onboarded || false,
            platform: platform,
            protocol: protocol,
            protocolPort: protocolPort,
            ...resource,
          };
        });
        
        const sortedResources = resources.sort((a, b) => {
          const aStatus = String(a.status || "").toUpperCase();
          const bStatus = String(b.status || "").toUpperCase();
          const aIsRunning = aStatus === "RUNNING";
          const bIsRunning = bStatus === "RUNNING";
          const aIsOnboarded = a.is_onboarded === true;
          const bIsOnboarded = b.is_onboarded === true;
          
          const aRunningOnboarded = aIsRunning && aIsOnboarded;
          const bRunningOnboarded = bIsRunning && bIsOnboarded;
          if (aRunningOnboarded && !bRunningOnboarded) return -1;
          if (!aRunningOnboarded && bRunningOnboarded) return 1;
          
          if (aIsRunning && !bIsRunning) return -1;
          if (!aIsRunning && bIsRunning) return 1;
          
          if (aIsOnboarded && !bIsOnboarded) return -1;
          if (!aIsOnboarded && bIsOnboarded) return 1;
          
          return 0;
        });
        
        setResultsList(sortedResources);
        setList(sortedResources);
        setFilteredData(sortedResources);
        setTotal(data.count || data.total || sortedResources.length);
      }
    } catch (e) {
      showError("An unexpected error occurred while fetching scan results.");
    } finally {
      setPending(false);
    }
  };


  const handlePageChange = (p) => setPage(p);
  const handlePerRowsChange = async (newPerPage) => {
    setRowsPerPage(newPerPage);
    setPage(1);
    await PutColumnPreference(checkedStateDict, pages_url, newPerPage);
  };

  const refreshRowStatus = useCallback(async (row) => {
    const rowId = row.id || row.instanceid || row.provider_resource_id;
    if (!rowId || !scanId) {
      return;
    }

    setRefreshingStatusRowId(rowId);

    try {
      const { data, error } = await refreshResourceStatus({
        resource_id: row.id,
        scan_id: scanId
      });

      if (error) {
        console.error("Failed to refresh status:", error);
        showError(error?.message?.[0] || error?.message || "Failed to refresh resource status");
        setRefreshingStatusRowId(null);
        return;
      }

      if (data && data.resource) {
        const updatedResource = data.resource;
        const metadata = updatedResource.metadata || {};
        const platform = metadata.platform || row.platform || "";
        
        let protocol = "-";
        let protocolPort = null;
        if (platform.toLowerCase().includes("windows")) {
          protocol = "RDP";
          protocolPort = 3389;
        } else if (platform.toLowerCase().includes("linux") || platform.toLowerCase().includes("unix")) {
          protocol = "SSH/SFTP";
          protocolPort = 22;
        }

        const transformedResource = {
          ...row,
          id: updatedResource.id,
          status: updatedResource.status || metadata.state || metadata.status || row.status,
          metadata: metadata,
          publicip: metadata.public_ip || metadata.publicip || row.publicip,
          privateip: metadata.private_ip || metadata.privateip || row.privateip,
          keyname: metadata.key_name || metadata.keyname || row.keyname,
          region: metadata.region || row.region,
          platform: platform,
          protocol: protocol,
          protocolPort: protocolPort,
        };

        setList(prevList => {
          const newList = prevList.map(item => {
            const itemId = item.id || item.instanceid || item.provider_resource_id;
            if (itemId === rowId || item.id === updatedResource.id) {
              return transformedResource;
            }
            return item;
          });
          return newList;
        });

        setFilteredData(prevFilteredData => {
          const newFilteredData = prevFilteredData.map(item => {
            const itemId = item.id || item.instanceid || item.provider_resource_id;
            if (itemId === rowId || item.id === updatedResource.id) {
              return transformedResource;
            }
            return item;
          });
          return newFilteredData;
        });

        setResultsList(prevResultsList => {
          const newResultsList = prevResultsList.map(item => {
            const itemId = item.id || item.instanceid || item.provider_resource_id;
            if (itemId === rowId || item.id === updatedResource.id) {
              return transformedResource;
            }
            return item;
          });
          return newResultsList;
        });
      }
    } catch (error) {
      console.error("Exception refreshing status:", error);
      showError(t("cloudDiscovery.errorRefreshingStatus"));
    } finally {
      setTimeout(() => {
        setRefreshingStatusRowId(null);
      }, 300);
    }
  }, [scanId]);

  const refreshRowReachability = useCallback(async (row) => {
    const rowId = row.id || row.instanceid;
    const publicIP = row.publicip;
    const privateIP = row.privateip;
    const protocol = row.protocol || "";
    
    const ipToCheck = publicIP && publicIP !== "-" ? publicIP : (privateIP && privateIP !== "-" ? privateIP : null);
    
    if (!ipToCheck) {
      return;
    }

    // Determine primary port based on protocol
    let primaryPort = 80; // Default to HTTP
    if (protocol.toUpperCase().includes("RDP")) {
      primaryPort = 3389;
    } else if (protocol.toUpperCase().includes("SSH")) {
      primaryPort = 22;
    }

    setReachabilityStatus(prev => ({
      ...prev,
      [rowId]: "checking"
    }));

    try {
      const machineStatus = row.status || row.metadata?.state || row.metadata?.status;
      const { data, error } = await checkIPReachability({
        ip_address: ipToCheck,
        port: primaryPort,
        machine_status: machineStatus
      });

      if (error) {
        console.error("Reachability refresh error:", error);
        setReachabilityStatus(prev => ({
          ...prev,
          [rowId]: "unreachable"
        }));
        return;
      }

      if (data && data.is_reachable !== undefined) {
        setReachabilityStatus(prev => ({
          ...prev,
          [rowId]: data.is_reachable ? "reachable" : "unreachable"
        }));
      } else {
        setReachabilityStatus(prev => ({
          ...prev,
          [rowId]: "unreachable"
        }));
      }
    } catch (error) {
      console.error("Exception refreshing reachability:", error);
      setReachabilityStatus(prev => ({
        ...prev,
        [rowId]: "unreachable"
      }));
    }
  }, []);

  const checkBatchReachability = useCallback(async (rowsToCheck) => {
    if (!rowsToCheck || rowsToCheck.length === 0) {
      return;
    }

    const batchChecks = rowsToCheck.map(row => {
      const rowId = row.id || row.instanceid;
      const publicIP = row.publicip;
      const privateIP = row.privateip;
      const protocol = row.protocol || "";
      
      const ipToCheck = publicIP && publicIP !== "-" ? publicIP : (privateIP && privateIP !== "-" ? privateIP : null);
      
      if (!ipToCheck) {
        return null;
      }

      let portsToCheck = [80, 443];
      
      if (protocol.toUpperCase().includes("RDP")) {
        portsToCheck = [80, 443, 3389];
      } else if (protocol.toUpperCase().includes("SSH")) {
        portsToCheck = [80, 443, 22];
      }

      const machineStatus = row.status || row.metadata?.state || row.metadata?.status;
      return {
        ip_address: ipToCheck,
        ports: portsToCheck,
        rowId: rowId,
        machine_status: machineStatus
      };
    }).filter(check => check !== null);

    if (batchChecks.length === 0) {
      return;
    }

    setReachabilityStatus(prev => {
      const newStatus = { ...prev };
      batchChecks.forEach(check => {
        newStatus[check.rowId] = "checking";
      });
      return newStatus;
    });

    try {
      const apiChecks = batchChecks.map(({ ip_address, ports, machine_status }) => ({
        ip_address,
        ports,
        machine_status
      }));

      const { data, error } = await checkBatchIPReachability(apiChecks, 3);

      if (error) {
        console.error("Batch reachability check error:", error);
        setReachabilityStatus(prev => {
          const newStatus = { ...prev };
          batchChecks.forEach(check => {
            newStatus[check.rowId] = "unreachable";
          });
          return newStatus;
        });
        return;
      }

      if (data && data.results) {
        setReachabilityStatus(prev => {
          const newStatus = { ...prev };
          
          batchChecks.forEach(check => {
            const ipAddress = check.ip_address;
            const rowId = check.rowId;
            const results = data.results[ipAddress];
            
            if (results) {
              const anyReachable = Object.values(results).some(
                portResult => portResult.is_reachable === true
              );
              
              newStatus[rowId] = anyReachable ? "reachable" : "unreachable";
            } else {
              newStatus[rowId] = "unreachable";
            }
          });
          
          return newStatus;
        });
      }
    } catch (error) {
      console.error("Exception in batch reachability check:", error);
      setReachabilityStatus(prev => {
        const newStatus = { ...prev };
        batchChecks.forEach(check => {
          newStatus[check.rowId] = "unreachable";
        });
        return newStatus;
      });
    }
  }, []);

  const processCheckingQueue = useCallback(async () => {
    if (isCheckingRef.current || checkingQueueRef.current.length === 0) {
      return;
    }

    isCheckingRef.current = true;
    
    const BATCH_SIZE = 10;
    const batch = checkingQueueRef.current.splice(0, BATCH_SIZE);
    
    if (batch.length > 0) {
      await checkBatchReachability(batch);
      
      setTimeout(() => {
        isCheckingRef.current = false;
        processCheckingQueue();
      }, 500);
    } else {
      isCheckingRef.current = false;
    }
  }, [checkBatchReachability]);

  useEffect(() => {
    if (list.length === 0) return;

    const visibleRows = filteredData.length > 0 ? filteredData : list;
    const allRows = list;

    const visibleRowsToCheck = visibleRows.filter(row => {
      const rowId = row.id || row.instanceid;
      const publicIP = row.publicip;
      const privateIP = row.privateip;
      const hasIP = (publicIP && publicIP !== "-") || (privateIP && privateIP !== "-");
      const status = reachabilityStatus[rowId];
      
      return hasIP && status !== "checking" && status !== "reachable" && status !== "unreachable";
    });

    const otherRowsToCheck = allRows.filter(row => {
      const rowId = row.id || row.instanceid;
      const publicIP = row.publicip;
      const privateIP = row.privateip;
      const hasIP = (publicIP && publicIP !== "-") || (privateIP && privateIP !== "-");
      const status = reachabilityStatus[rowId];
      
      const isVisible = visibleRows.some(r => (r.id || r.instanceid) === rowId);
      if (isVisible) return false;
      
      return hasIP && status !== "checking" && status !== "reachable" && status !== "unreachable";
    });

    checkingQueueRef.current = [];

    visibleRowsToCheck.forEach(row => {
      const rowId = row.id || row.instanceid;
      if (!checkingQueueRef.current.find(r => (r.id || r.instanceid) === rowId)) {
        checkingQueueRef.current.push(row);
      }
    });

    otherRowsToCheck.forEach(row => {
      const rowId = row.id || row.instanceid;
      if (!checkingQueueRef.current.find(r => (r.id || r.instanceid) === rowId)) {
        checkingQueueRef.current.push(row);
      }
    });

    if (!isCheckingRef.current && checkingQueueRef.current.length > 0) {
      processCheckingQueue();
    }
  }, [list, filteredData, reachabilityStatus]);

  useEffect(() => {
    return () => {
      if (checkingIntervalRef.current) {
        clearInterval(checkingIntervalRef.current);
      }
    };
  }, []);

  const columns = useMemo(() => [
    {
      name: t("cloudDiscovery.instanceId"),
      selector: (row) => row.instanceid || "-",
      sortable: true,
    },
    {
      name: t("common.name"),
      selector: (row) => row.name || "-",
      sortable: true,
    },
    {
      name: t("common.type"),
      cell: (row) => {
        const type = row.type || row.service || "-";
        
        if (type === "-") {
          return <span>-</span>;
        }
        
        return (
          <div className="cloud-discovery-tags-container">
            <span className="cloud-discovery-tag">
              {type}
            </span>
          </div>
        );
      },
      selector: (row) => row.type || row.service || "-",
      sortable: true,
      omit: checkedStateDict["common.type"] === false,
    },
    {
      name: t("common.status"),
      cell: (row) => {
        const rowId = row.id || row.instanceid || row.provider_resource_id;
        const isRefreshing = refreshingStatusRowId === rowId;
        const status = row.status || "-";
        
        if (status === "-") {
          return <span>-</span>;
        }
        
        if (isRefreshing) {
          return (
            <div className="d-flex align-items-center justify-content-between w-100">
              <Skeleton 
                variant="rounded" 
                width={100} 
                height={24} 
                sx={{ borderRadius: "4px" }}
              />
              <div className="d-flex align-items-center gap-2">
                <Tooltip title={t("cloudDiscovery.refreshStatus")} arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      refreshRowStatus(row);
                    }}
                    sx={{
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(81, 119, 255, 0.1)",
                      },
                    }}
                  >
                    <FiRefreshCw 
                      size={16} 
                      style={{ color: "#5177FF" }}
                    />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          );
        }
        
        const upperStatus = String(status).toUpperCase();
        let statusText = status;
        let statusClass = "";
        
        if (upperStatus === "RUNNING" || upperStatus === "ACTIVE" || upperStatus === "AVAILABLE") {
          statusText = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
          statusClass = "cloud-discovery-status-badge cloud-discovery-status-active";
        } else if (upperStatus === "STOPPED" || upperStatus === "STOPPING" || upperStatus === "TERMINATED" || upperStatus === "FAILED" || upperStatus === "ERROR") {
          statusText = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
          statusClass = "cloud-discovery-status-badge cloud-discovery-status-inactive";
        } else if (upperStatus === "PENDING" || upperStatus === "PROVISIONING" || upperStatus === "IN_PROGRESS") {
          statusText = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
          statusClass = "cloud-discovery-status-badge cloud-discovery-status-partially-active";
        } else {
          statusText = status;
          statusClass = "cloud-discovery-status-badge cloud-discovery-status-partially-active";
        }
        
        return (
          <div className="d-flex align-items-center justify-content-between w-100">
          <span className={statusClass} style={{ fontWeight: 600 }}>
            {statusText}
          </span>
            <div className="d-flex align-items-center gap-2">
              <Tooltip title="Refresh Status" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    refreshRowStatus(row);
                  }}
                  sx={{
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(81, 119, 255, 0.1)",
                    },
                  }}
                >
                  <FiRefreshCw 
                    size={16} 
                    style={{ color: "#5177FF" }}
                  />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        );
      },
      selector: (row) => row.status || "-",
      sortable: true,
      omit: checkedStateDict["reports.status"] === false,
      grow: 1,
    },
    {
      name: t("ldap.protocol"),
      cell: (row) => {
        const protocol = row.protocol || "-";
        
        if (protocol === "-") {
          return <span>-</span>;
        }
        
        return (
          <div className="cloud-discovery-tags-container">
            <span className="cloud-discovery-tag">
              {protocol}
            </span>
          </div>
        );
      },
      selector: (row) => row.protocol || "-",
      sortable: true,
      omit: checkedStateDict["siem.protocol"] === false,
    },
    {
      name: t("cloudDiscovery.region"),
      cell: (row) => {
        const region = Array.isArray(row.region) ? row.region.join(", ") : row.region || "-";
        return <div className="hover-expand">{region}</div>;
      },
      sortable: true,
      omit: checkedStateDict["cloudDiscovery.region"] === false,
    },
    {
      name: t("cloudDiscovery.publicIp"),
      selector: (row) => row.publicip || "-",
      sortable: true,
      omit: checkedStateDict["cloudDiscovery.publicIp"] === false,
    },
    {
      name: t("cloudDiscovery.privateIp"),
      selector: (row) => row.privateip || "-",
      sortable: true,
      omit: checkedStateDict["cloudDiscovery.privateIp"] === false,
    },
    {
      name: t("cloudDiscovery.onboarded"),
      selector: (row) => row.is_onboarded ? "Yes" : "No",
      sortable: true,
      omit: checkedStateDict["networkConfiguration.onboarded"] === false,
    },
    {
      name: t("cloudDiscovery.reachable"),
      cell: (row) => {
        const rowId = row.id || row.instanceid;
        const status = reachabilityStatus[rowId];
        const publicIP = row.publicip;
        const privateIP = row.privateip;
        const hasIP = (publicIP && publicIP !== "-") || (privateIP && privateIP !== "-");

        if (!hasIP) {
          return <span style={{ color: "#717171" }}>-</span>;
        }

        if (status === "checking") {
          return (
            <div className="d-flex align-items-center w-100">
              <div className="d-flex align-items-center gap-2">
                <div 
                  className="spinner-border spinner-border-sm" 
                  role="status"
                  style={{ 
                    width: "14px", 
                    height: "14px", 
                    borderWidth: "2px",
                    borderColor: "#5177FF",
                    borderRightColor: "transparent"
                  }}
                >
                  <span className="visually-hidden">{t("cloudDiscovery.checking")}</span>
                </div>
                <span style={{ fontSize: "12px", color: "#717171" }}>{t("cloudDiscovery.checking")}</span>
              </div>
            </div>
          );
        }

        if (status === "reachable") {
          return (
            <div className="d-flex align-items-center justify-content-between w-100">
              <span
                className="cloud-discovery-status-badge cloud-discovery-status-active"
                style={{ fontWeight: 600 }}
              >
                {t("cloudDiscovery.reachable")}
              </span>
              <div className="d-flex align-items-center gap-2">
                <Tooltip title={t("cloudDiscovery.refreshReachability")} arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      refreshRowReachability(row);
                    }}
                    sx={{
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(81, 119, 255, 0.1)",
                      },
                    }}
                  >
                    <FiRefreshCw 
                      size={16} 
                      style={{ color: "#5177FF" }}
                    />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          );
        }

        if (status === "unreachable") {
          return (
            <div className="d-flex align-items-center justify-content-between w-100">
              <span
                className="cloud-discovery-status-badge cloud-discovery-status-inactive"
                style={{ fontWeight: 600 }}
              >
                {t("cloudDiscovery.unreachable")}
              </span>
              <div className="d-flex align-items-center gap-2">
                <Tooltip title={t("cloudDiscovery.refreshReachability")} arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      refreshRowReachability(row);
                    }}
                    sx={{
                      padding: "4px",
                      "&:hover": {
                        backgroundColor: "rgba(81, 119, 255, 0.1)",
                      },
                    }}
                  >
                    <FiRefreshCw 
                      size={16} 
                      style={{ color: "#5177FF" }}
                    />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          );
        }

        return (
          <div className="d-flex align-items-center justify-content-between w-100">
            <span
              className="cloud-discovery-status-badge cloud-discovery-status-partially-active"
              style={{ fontWeight: 600 }}
            >
              {t("cloudDiscovery.pending")}
            </span>
            <div className="d-flex align-items-center gap-2">
              <Tooltip title="Refresh Reachability" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    refreshRowReachability(row);
                  }}
                  sx={{
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(81, 119, 255, 0.1)",
                    },
                  }}
                >
                  <FiRefreshCw 
                    size={16} 
                    style={{ color: "#5177FF" }}
                  />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        );
      },
      selector: (row) => {
        const rowId = row.id || row.instanceid;
        const status = reachabilityStatus[rowId];
        if (status === "reachable") return "Reachable";
        if (status === "unreachable") return "Unreachable";
        if (status === "checking") return "Checking";
        return "Pending";
      },
      sortable: true,
      grow: 1,
      minWidth: "170px",
      omit: checkedStateDict["cloudDiscovery.reachable"] === false,
    },
  ], [checkedStateDict, reachabilityStatus, refreshRowReachability, refreshRowStatus, refreshingStatusRowId]);

  const handleRowSelected = useCallback((state) => {
    setSelectedRows(state.selectedRows || []);
  }, []);

  const handleOnboard = async () => {
    if (selectedRows.length === 0) {
      showError(t("cloudDiscovery.pleaseSelectAtLeastOneResourceToOnboard"));
      return;
    }

    try {
      setPending(true);
      const providerResourceIds = selectedRows
        .map(row => row.provider_resource_id || row.instanceid || row.id)
        .filter(Boolean);

      if (providerResourceIds.length === 0) {
        showError(t("cloudDiscovery.noValidResourceIds"));
        setPending(false);
        return;
      }

      const { data, error } = await onboardAssets({
        provider_resource_ids: providerResourceIds,
        scan_id: parseInt(scanId),
      });

      if (error) {
        showError(error?.message?.[0] || error?.message || t("cloudDiscovery.failedToOnboardAssets"));
      } else {
        const created = data?.total_created || 0;
        const duplicates = data?.total_duplicates || 0;
        const errors = data?.total_errors || 0;

        if (created > 0) {
          showSuccess(t("cloudDiscovery.assetsOnboardedSuccessfully", { count: created }) + (duplicates > 0 ? `, ${duplicates} already onboarded` : '') + (errors > 0 ? `, ${errors} failed` : ''));
          navigate("/dashboard/resources");
        } else if (duplicates > 0) {
          showSuccess(t("cloudDiscovery.allAssetsAlreadyOnboarded"));
        } else {
          showError(t("cloudDiscovery.failedToOnboardAssets"));
        }

        setToggleCleared(!toggleCleared);
        setSelectedRows([]);
        await fetchScanResults();
      }
    } catch (err) {
      showError(t("cloudDiscovery.unexpectedOnboardError"));
    } finally {
      setPending(false);
    }
  };

  const handleEditAndOnboard = (rows) => {
    const rowsToEditOnboard = rows ? rows : selectedRows;
    if (rowsToEditOnboard.length === 0) {
      showError(t("cloudDiscovery.pleaseSelectAtLeastOneResourceToEditAndOnboard"));
      return;
    }
    setResourcesToEditOnboard(rowsToEditOnboard);
    setResourceNames(rowsToEditOnboard.map((item) => item.name || item.instanceid || "Unnamed Resource"));
    setOpenEditOnboardModal(true);
  };

  const handleEditAndOnboardAction = async () => {
    try {
      setPending(true);
      const providerResourceIds = resourcesToEditOnboard
        .map(row => row.provider_resource_id || row.instanceid || row.id)
        .filter(Boolean);

      if (providerResourceIds.length === 0) {
        showError(t("cloudDiscovery.noValidResourceIds"));
        setPending(false);
        return;
      }

      const { data, error } = await onboardAssets({
        provider_resource_ids: providerResourceIds,
        scan_id: parseInt(scanId),
      });

      if (error) {
        showError(error?.message?.[0] || error?.message || t("cloudDiscovery.failedToOnboardAssets"));
      } else {
        const created = data?.total_created || 0;
        const duplicates = data?.total_duplicates || 0;
        const errors = data?.total_errors || 0;

        if (created > 0) {
          showSuccess(t("cloudDiscovery.assetsOnboardedSuccessfully", { count: created }) + (duplicates > 0 ? `, ${duplicates} already onboarded` : '') + (errors > 0 ? `, ${errors} failed` : ''));
          navigate("/dashboard/resources");
        } else if (duplicates > 0) {
          showSuccess(t("cloudDiscovery.allAssetsAlreadyOnboarded"));
        } else {
          showError(t("cloudDiscovery.failedToOnboardAssets"));
        }

        setToggleCleared(!toggleCleared);
        setSelectedRows([]);
        await fetchScanResults();
      }
    } catch (err) {
      showError(t("cloudDiscovery.unexpectedOnboardError"));
    } finally {
      setPending(false);
      setOpenEditOnboardModal(false);
      setResourcesToEditOnboard([]);
      setResourceNames([]);
    }
  };

  const handleAction = async () => {
    if (action === "edit_and_onboard") {
      handleEditAndOnboard(null);
    }
  };

  useEffect(() => {
    document.title = t("cloudDiscovery.scanResult");
    setSlideChecked(true);
  }, []);

  useEffect(() => {
    if (!preferenceLoaded) return;
    fetchScanResults();
  }, [scanId, preferenceLoaded]);

  useEffect(() => {
    if (!list) return;
    const s = search.replaceAll(/[\\*()+[?]/g, "");
    const result = list.filter((o) => {
      if (!filterBy) {
        return (
          String(o.instanceid || "").toLowerCase().includes(s.toLowerCase()) ||
          String(o.name || "").toLowerCase().includes(s.toLowerCase()) ||
          String(o.type || o.service || "").toLowerCase().includes(s.toLowerCase()) ||
          String(o.protocol || "").toLowerCase().includes(s.toLowerCase()) ||
          String(o.status || "").toLowerCase().includes(s.toLowerCase()) ||
          String(o.region || "").toLowerCase().includes(s.toLowerCase()) ||
          String(o.publicip || "").toLowerCase().includes(s.toLowerCase()) ||
          String(o.privateip || "").toLowerCase().includes(s.toLowerCase())
        );
      }
      return String(o[filterBy] || "").toLowerCase().includes(s.toLowerCase());
    });
    
    const sortedResult = result.sort((a, b) => {
      const aStatus = String(a.status || "").toUpperCase();
      const bStatus = String(b.status || "").toUpperCase();
      const aIsRunning = aStatus === "RUNNING";
      const bIsRunning = bStatus === "RUNNING";
      const aIsOnboarded = a.is_onboarded === true;
      const bIsOnboarded = b.is_onboarded === true;
      
      const aRunningOnboarded = aIsRunning && aIsOnboarded;
      const bRunningOnboarded = bIsRunning && bIsOnboarded;
      if (aRunningOnboarded && !bRunningOnboarded) return -1;
      if (!aRunningOnboarded && bRunningOnboarded) return 1;
      
      if (aIsRunning && !bIsRunning) return -1;
      if (!aIsRunning && bIsRunning) return 1;
      
      if (aIsOnboarded && !bIsOnboarded) return -1;
      if (!aIsOnboarded && bIsOnboarded) return 1;
      
      return 0;
    });
    
    setFilteredData(sortedResult);
  }, [search, list, filterBy]);

  useEffect(() => {
    const div = document.getElementById("cloudDatatableDivId");
    if (div && list && list.length > 0) {
      div.style.height = tableHeightWithData;
      setDatatableHeight(tableHeightWithData);
    } else {
      setDatatableHeight(tableHeightEmpty);
    }
  }, [list]);

  return (
    <>
      {!preferenceLoaded || (pending && list.length === 0) ? (
        <DataTableSkeleton rowCount={7} columnCount={8} />
      ) : (
        <Slide timeout={delayTime} direction={slideDirection} in={slideChecked}>
          <Fade timeout={fadedelayTime} in={slideChecked}>
          <div className="main_content_container p-3 w-100">
            <div
              className="position-relative bg-white"
              id="cloudDatatableDivId"
              style={{ height: datatableHeight }}
            >
                  <DataTable
                    title={
                <>
                  <div className="py-2 pt-2">
                    <BreadcrumbHeading
                      dataTableHeadingText={[
                        t("cloudDiscovery.backToCloudDiscovery"),
                        t("cloudDiscovery.scanHistory"),
                        t("cloudDiscovery.scanResult"),
                      ]}
                      dataTablePageLink={[
                        "/dashboard/discovery/cloudconfiguration",
                        configId ? `/dashboard/discovery/cloudconfiguration/history/${configId}` : `/dashboard/discovery/cloudconfiguration/history/${scanId}`,
                      ]}
                      pageNumber={3}
                    />
                  </div>
                  <div className="mb-3">
                    <ListHeading
                      dataTableHeadingText={page_heading}
                      dataTableHeadingIcon={Images.NetworkAnalyzer}
                    />
                  </div>
                </>
              }
              columns={columns}
              data={filteredData}
              className="rdt_container"
              selectableRows
              selectableRowsVisibleOnly
              selectableRowsHighlight
              selectableRowDisabled={(row) => {
                if (row.is_onboarded) {
                  return true;
                }
                const status = String(row.status || "").toUpperCase();
                const type = String(row.type || row.service || "").toUpperCase();
                const isRunning = status === "RUNNING";
                const isEC2 = type === "EC2" || type === "EC2-INSTANCE";
                return !(isRunning && isEC2);
              }}
              onSelectedRowsChange={handleRowSelected}
              clearSelectedRows={toggleCleared}
              customStyles={{
                cells: {
                  style: {
                    paddingLeft: '20px',
                    paddingRight: '20px',
                  },
                },
                headCells: {
                  style: {
                    paddingLeft: '20px',
                    paddingRight: '20px',
                  },
                },
                table: {
                  style: {
                    borderCollapse: 'separate',
                    borderSpacing: '0',
                  },
                },
              }}
              progressPending={pending}
              progressComponent={
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                  <div style={{ color: '#5177ff' }} className="la-line-scale-pulse-out-rapid la-3x">
                    <div className="rounded"></div>
                    <div className="rounded"></div>
                    <div className="rounded"></div>
                    <div className="rounded"></div>
                    <div className="rounded"></div>
                  </div>
                </div>
              }
              fixedHeader
              paginationPerPage={rowsPerPage}
              paginationRowsPerPageOptions={[5, 10, 25, 50, 100]}
              pagination
              paginationServer
              paginationTotalRows={total}
              highlightOnHover
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              subHeader
              subHeaderComponent={
                <SubHeaderComponent
                  filterBy={filterBy}
                  setFilterBy={setFilterBy}
                  search={search}
                  handleSearch={handleSearch}
                  setSearch={setSearch}
                  filterOptions={filterOptions}
                  placeholder="For a scan history"
                  pages_url={pages_url}
                  checkedStateDict={checkedStateDict}
                  setCheckedStateDict={setCheckedStateDict}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  action={action}
                  setAction={setAction}
                  handleAction={handleAction}
                  actionOptions={actionOptions}
                  selectedRows={selectedRows}
                />
              }
              subHeaderAlign="center"
            />
            </div>
          </div>
        </Fade>
      </Slide>
      )}
      {resourceNames && resourceNames.length > 0 && (
        <CustomModal
          open={openEditOnboardModal}
          handleClose={() => {
            setOpenEditOnboardModal(false);
            setResourceNames([]);
            setResourcesToEditOnboard([]);
          }}
        >
          <ShowAlert
            setOpenAlert={(value) => {
              setOpenEditOnboardModal(value);
              if (!value) {
                setResourceNames([]);
                setResourcesToEditOnboard([]);
              }
            }}
            handleAlertAction={handleEditAndOnboardAction}
            colName={resourceNames}
            alertMsg={t("cloudDiscovery.belowResourcesWillBeOnboarded")}
            headingMsg={t("cloudDiscovery.onboardResources")}
            loading={pending}
          />
        </CustomModal>
      )}
    </>
  );
}
